import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Sidebar from "../../Components/Sidebar";
import Topbar from "../../Components/Topbar";
import Tabs from "../../Components/Tabs";
import { Link } from "react-router-dom";
import { getAssistantById } from "../../actions/assistants";
import { RoutesPaths } from "../../Router/constant";
import { RobotIcon } from "../Auth/onboarding/icons";
import AddToWebsiteCard from "./AddToWebsiteCard";
import PreviewCard from "./PreviewCard";
import InstallationStatusCard from "./InstallationStatusCard";
import RecrawlCard from "./RecrawlCard";
import DomainSettingsCard from "./DomainSettingsCard";
import AdvancedSettingsCard from "./AdvancedSettingsCard";
import NotificationEmailCard from "./NotificationEmailCard";
import DeleteAssistantCard from "./DeleteAssistantCard";
import SecurityBanner from "./SecurityBanner";
import LoadingState from "../../Components/LoadingState";

const tabs = ["Installation", "Widget settings", "Targeting", "Security", "Advanced"];

export default function InstallSettings() {
  const [activeTab, setActiveTab] = useState("Installation");
  // Set by the sidebar switcher, same as the rest of the app.
  const assistantId = useSelector((state) => state.assistants.activeId);
  const dispatch = useDispatch();

  const [assistant, setAssistant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // The account is paused - an unpaid renewal, or a subscription that ended.
  // Nothing here can be changed until that is settled, but an assistant can
  // still be deleted, which is how an account gets small enough for a plan.
  const [paused, setPaused] = useState(false);

  const load = useCallback(async () => {
    if (!assistantId) {
      setAssistant(null);
      return;
    }

    setLoading(true);
    const result = await dispatch(getAssistantById(assistantId));
    setLoading(false);

    if (!result?.success) {
      setPaused(Boolean(result?.paused));
      // The paused notice says it better than a failed-request message.
      setError(
        result?.paused
          ? ""
          : result?.message || "We couldn't load your install settings.",
      );
      return;
    }

    setPaused(false);
    setError("");
    setAssistant(result.assistant);
  }, [assistantId, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const renderInstallation = () => {
    if (!assistantId) {
      return (
        <div className="asst-list-state">
          <span className="asst-list-empty-icon">
            <RobotIcon />
          </span>
          <strong>No assistant selected</strong>
          <p>Pick one from the switcher at the top of the sidebar.</p>
        </div>
      );
    }

    if (loading && !assistant) {
      return (
        <LoadingState
          title="Loading your install settings"
          detail="Checking where this assistant is installed."
        />
      );
    }

    // Paused: the install snippet, the re-crawl and the domain settings are
    // all changes to an assistant that is not being served, and the API
    // refuses them. Deleting is the exception - it still works, and it is the
    // one thing someone here to shrink their account needs.
    if (paused) {
      return (
        <>
          <section className="dash-card ist-paused-card">
            <div className="dash-card-header">
              <h2 className="dash-card-title">Your account is paused</h2>
            </div>
            <p className="ist-paused-card__text">
              Install and widget settings are read-only until your subscription
              is active again. You can still delete assistants here — removing
              one frees its slot, which is what a smaller plan needs.
            </p>
            <Link
              to={RoutesPaths.BILLING}
              className="onboarding__btn onboarding__btn--primary"
            >
              Go to Billing
            </Link>
          </section>

          <div className="ist-grid-3">
            <DeleteAssistantCard assistant={assistant || { id: assistantId }} />
          </div>
        </>
      );
    }

    return (
      <>
        {error && <p className="auth-error">{error}</p>}

        {/* One grid, not two: the second row's cards line up under the
            first row's columns only if they are placed by the same grid.
            Reading order here is the order the page is worked through, which
            is also what a single column falls back to on a narrow screen. */}
        <div className="ist-grid-3">
          <AddToWebsiteCard assistant={assistant} />
          <PreviewCard assistant={assistant} />
          {/* The card runs its own installation-status request; reloading
              the record alongside it keeps the domain and crawl source in
              step with what the check reports. */}
          <InstallationStatusCard assistant={assistant} onRecheck={load} />

          {/* onFinished refreshes the record so the status badge and the
              install checks reflect the crawl that just ended. */}
          <RecrawlCard assistant={assistant} onFinished={load} />
          <DomainSettingsCard assistant={assistant} onSaved={load} />

          {/* Two short cards share the third column, stacked, rather than
              leaving one of them to start a row of its own with two empty
              cells beside it. */}
          <div className="ist-stack">
            {/* Where this assistant's emails go, which can differ per assistant. */}
            <NotificationEmailCard assistant={assistant} onSaved={load} />
            {/* Removing the assistant. No onChanged: the action refreshes the
                list itself and the card leaves for the dashboard. */}
            <DeleteAssistantCard assistant={assistant} />
          </div>
          {/* <AdvancedSettingsCard /> */}
        </div>

        <SecurityBanner />
      </>
    );
  };

  return (
    <div className="dash">
      <Sidebar />

      <div className="dash-main">
        <Topbar
          title="Install & Settings"
          subtitle="Install Hostor on your website and configure how your assistant works."
        />

        <div className="dash-content">
          {/* <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} /> */}

          {activeTab === "Installation" ? (
            renderInstallation()
          ) : (
            <section className="dash-card onboarding__card--placeholder">
              <h2 className="onboarding__card-title">{activeTab} is coming soon</h2>
              <p className="onboarding__card-subtitle">
                We&apos;re still building this tab. Check back soon, or head back to Installation.
              </p>
              <button
                type="button"
                className="onboarding__btn onboarding__btn--primary"
                onClick={() => setActiveTab("Installation")}
              >
                Back to Installation
              </button>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
